<script>
import { mapState } from "vuex";
import StKpiCard from "@/components/base/st-kpi-card";
import { api } from "@/state/services";
import { carregarConfigMetas, produtosVisiveis, temApvs, nomeMes } from "@/utils/minhas-metas";
import { canAccessRegionalMenu } from "@/utils/regional-user-permissions";
import {
  RANKINGS_NACIONAIS_POR_ID,
  INDICADORES_NACIONAL,
  ORDEM_DESEMPATE,
  fmtIndicador,
  isConsultor,
  podeVerRanking,
} from "@/utils/ranking-nacional";

// Ranking de onde saem os "Seus números no período" de cada perfil/produto. Todos os rankings da
// mesma entidade devolvem os mesmos indicadores em `eu.indicadores`; escolhemos o de população
// mais ampla (Regional Adesão = todas as regionais; Consultores = base menor que 300).
const RANKING_RESUMO = {
  regional: { brasil: "nacional-brasil-regional-adesao", truck: "nacional-truck-regional-adesao" },
  consultor: { brasil: "nacional-brasil-consultores-boletos", truck: "nacional-truck-consultor-adesao" },
};

// O 1º cálculo do período no servidor responde 202 "processing" (mesmo polling da tela do ranking,
// mais curto: aqui é só um resumo).
const POLL_INTERVALO_MS = 4000;
const POLL_MAX_TENTATIVAS = 15;

const NOMES = { brasil: "APVS Brasil", truck: "APVS Truck" };

/**
 * Resumo do Dashboard (abaixo do link de indicação):
 *   1. Adesões do mês (Feitas no mês / Meta pessoal / Dias restantes) — passo2 do GET metas/painel,
 *      os mesmos números do Passo 1 de Minhas Metas.
 *   2. Seus números no período — `eu.indicadores` do Ranking Nacional do mês corrente.
 * Cada bloco some sozinho quando o usuário não tem acesso (sem APVS, secretária sem permissão,
 * fora da população do ranking) ou quando a API falha: é um resumo, não pode travar a Home.
 */
export default {
  name: "DashboardResumo",
  components: { StKpiCard },
  data() {
    return {
      metas: {},
      carregandoMetas: false,
      numeros: {},
      carregandoNumeros: false,
    };
  },
  computed: {
    ...mapState({ account: (state) => state.account }),
    user() {
      return (this.account && this.account.user) || null;
    },
    podeVerMetas() {
      return !!this.user && temApvs(this.user) && canAccessRegionalMenu(this.user, "/minhas-metas");
    },
    produtosAtivos() {
      const m = (this.user && this.user.modules) || {};
      return ["brasil", "truck"].filter((p) => m["apvs_" + p] === "active");
    },
    blocosMetas() {
      return Object.keys(this.metas)
        .sort()
        .map((p) => ({ produto: p, nome: NOMES[p], ...this.metas[p] }));
    },
    blocosNumeros() {
      return this.produtosAtivos
        .filter((p) => this.numeros[p] && this.numeros[p].length)
        .map((p) => ({ produto: p, nome: NOMES[p], indicadores: this.numeros[p] }));
    },
    // Rótulo do produto só faz sentido para quem tem os dois.
    mostraProduto() {
      return this.produtosAtivos.length > 1;
    },
  },
  created() {
    this.timers = [];
    this.vivo = true;
    this.carregarMetas();
    this.carregarNumeros();
  },
  beforeDestroy() {
    this.vivo = false;
    this.timers.forEach((t) => clearTimeout(t));
  },
  methods: {
    nomeMes,
    fmtIndicador,
    async carregarMetas() {
      if (!this.podeVerMetas) return;
      this.carregandoMetas = true;
      try {
        const config = await carregarConfigMetas(this.$store);
        const produtos = produtosVisiveis(config);
        const respostas = await Promise.all(
          produtos.map((p) =>
            api
              .get("metas/painel?produto=" + p)
              .then((r) => ({ p, data: r && r.data }))
              .catch(() => ({ p, data: null }))
          )
        );
        if (!this.vivo) return;
        const out = {};
        respostas.forEach(({ p, data }) => {
          if (data && data.status === "success" && data.passo2) {
            out[p] = {
              mes: data.passo2.mes,
              feitas: data.passo2.feitas,
              meta: data.passo2.meta_pessoal,
              dias: data.passo2.dias_restantes,
              definida: data.regras && data.regras.meta_pessoal != null,
            };
          }
        });
        this.metas = out;
      } catch (e) {
        // Silencioso: o resumo some; Minhas Metas mostra o erro com "tentar de novo".
      } finally {
        this.carregandoMetas = false;
      }
    },
    carregarNumeros() {
      if (!this.user) return;
      const perfil = isConsultor(this.user) ? "consultor" : "regional";
      const pendentes = this.produtosAtivos
        .map((p) => ({ p, ranking: RANKINGS_NACIONAIS_POR_ID[RANKING_RESUMO[perfil][p]] }))
        .filter((x) => x.ranking && podeVerRanking(this.user, x.ranking));
      if (!pendentes.length) return;
      this.carregandoNumeros = true;
      let restantes = pendentes.length;
      const fim = () => {
        restantes--;
        if (restantes <= 0) this.carregandoNumeros = false;
      };
      pendentes.forEach(({ p, ranking }) => this.buscarRanking(p, ranking, 0, fim));
    },
    buscarRanking(produto, ranking, tentativa, fim) {
      api
        .get(ranking.endpoint)
        .then((res) => {
          if (!this.vivo) return;
          const corpo = (res && res.data) || {};
          if (res.status === 202 || corpo.status === "processing") {
            if (tentativa >= POLL_MAX_TENTATIVAS) return fim();
            this.timers.push(setTimeout(() => this.buscarRanking(produto, ranking, tentativa + 1, fim), POLL_INTERVALO_MS));
            return;
          }
          const eu = corpo.status === "success" ? corpo.eu : null;
          if (eu && eu.participa && eu.indicadores) {
            const lista = ORDEM_DESEMPATE.filter((k) => eu.indicadores[k] !== undefined && INDICADORES_NACIONAL[k]).map(
              (k) => ({ key: k, ...INDICADORES_NACIONAL[k], valor: eu.indicadores[k] })
            );
            this.$set(this.numeros, produto, lista);
          }
          fim();
        })
        .catch(() => fim());
    },
  },
};
</script>

<template>
  <div class="dr">
    <!-- Adesões do mês (meta pessoal) -->
    <section v-if="blocosMetas.length" class="card" aria-labelledby="dr-metas-titulo">
      <div class="card-body">
        <h2 id="dr-metas-titulo" class="dr-h2">
          Adesões de {{ nomeMes(blocosMetas[0].mes) }} (sua meta pessoal)
        </h2>
        <div v-for="b in blocosMetas" :key="b.produto" class="dr-bloco">
          <h3 v-if="mostraProduto" class="dr-produto">{{ b.nome }}</h3>
          <div class="dr-kpis">
            <StKpiCard label="Feitas no mês" :value="b.feitas" icon="bx bx-plus-circle" variant="brand" />
            <StKpiCard label="Meta pessoal" :value="b.meta" icon="bx bx-target-lock" variant="info"
              :hint="b.definida ? 'definida por você' : 'mínimo recomendado'" />
            <StKpiCard label="Dias restantes" :value="b.dias" icon="bx bx-calendar" variant="neutral" hint="contando hoje" />
          </div>
        </div>
        <router-link to="/minhas-metas" class="dr-link">
          Ver Minhas Metas <i class="bx bx-chevron-right" aria-hidden="true"></i>
        </router-link>
      </div>
    </section>
    <div v-else-if="carregandoMetas" class="card">
      <div class="card-body dr-kpis">
        <StKpiCard label="Feitas no mês" loading />
        <StKpiCard label="Meta pessoal" loading />
        <StKpiCard label="Dias restantes" loading />
      </div>
    </div>

    <!-- Seus números no período (Ranking) -->
    <section v-if="blocosNumeros.length" class="card" aria-labelledby="dr-numeros-titulo">
      <div class="card-body">
        <h2 id="dr-numeros-titulo" class="dr-sub">Seus números no período</h2>
        <div v-for="b in blocosNumeros" :key="b.produto" class="dr-bloco">
          <h3 v-if="mostraProduto" class="dr-produto">{{ b.nome }}</h3>
          <dl class="dr-indicadores">
            <div v-for="ind in b.indicadores" :key="ind.key" class="dr-ind">
              <dt :title="ind.label">{{ ind.curto }}</dt>
              <dd>{{ fmtIndicador(ind.valor, ind.fmt) }}</dd>
            </div>
          </dl>
        </div>
      </div>
    </section>
    <div v-else-if="carregandoNumeros" class="card">
      <div class="card-body">
        <h2 class="dr-sub">Seus números no período</h2>
        <div class="dr-carregando" role="status"><b-spinner small class="mr-2" /> Carregando…</div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.dr-h2 {
  margin: 0 0 1rem;
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--st-text);
}
.dr-sub {
  margin: 0 0 0.75rem;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--st-text-muted);
}
.dr-produto {
  margin: 0 0 0.5rem;
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--st-brand);
}
.dr-bloco + .dr-bloco {
  margin-top: 1rem;
}
.dr-kpis {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
}
.dr-link {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin-top: 0.75rem;
  font-size: 0.875rem;
  font-weight: 600;
}
.dr-indicadores {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
  gap: 8px;
  margin: 0;
}
.dr-ind {
  min-width: 0;
  padding: 8px 10px;
  border-radius: var(--st-radius);
  background: var(--st-canvas);
}
.dr-ind dt {
  font-size: 11px;
  font-weight: 600;
  color: var(--st-text-muted);
}
.dr-ind dd {
  margin: 2px 0 0;
  font-size: 18px;
  font-weight: 700;
  color: var(--st-text);
  white-space: nowrap;
}
.dr-carregando {
  font-size: 0.875rem;
  color: var(--st-text-muted);
}
@media (max-width: 575.98px) {
  .dr-kpis {
    grid-template-columns: minmax(0, 1fr);
    gap: 0.5rem;
  }
}
</style>
